import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SnackbarService } from '@services/support/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '@services/support/loader.service';
import { PublicUserPasswordService } from '@services/public-user-password.service';
import { buildPasswordRules, DEFAULT_LINK_PASSWORD_POLICY, PasswordRule, passwordRulesValidator } from './user-password-policy';

@Component({
    selector: 'app-user-password',
    templateUrl: './user-password.component.html',
    styleUrls: ['./user-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserPasswordComponent implements OnInit {
  passwordRules: PasswordRule[] = buildPasswordRules(DEFAULT_LINK_PASSWORD_POLICY);

  form = new FormGroup({
    password: new FormControl('', [Validators.required, passwordRulesValidator(() => this.passwordRules)]),
    confirmPassword: new FormControl('', [Validators.required])
  });

  linkId = '';
  loading = true;
  submitted = false;
  invalidLink = false;
  invalidLinkReason = 'LINK_INVALID';
  linkType = 'user-invite';
  userEmail = '';
  userUsername = '';
  userName = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private publicUserPasswordService: PublicUserPasswordService,
    private snackBarService: SnackbarService,
    private translateService: TranslateService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.linkId = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.linkId) {
      this.invalidLink = true;
      this.invalidLinkReason = 'LINK_INVALID';
      this.loading = false;
      return;
    }

    this.publicUserPasswordService.getLinkData(this.linkId)
      .then((result: any) => {
        this.linkType = result.linkType;
        this.userEmail = result.email;
        this.userUsername = result.username;
        this.userName = result.name;
        this.passwordRules = buildPasswordRules({ ...DEFAULT_LINK_PASSWORD_POLICY, ...(result.passwordPolicy || {}) });
        this.form.controls.password.updateValueAndValidity();
      })
      .catch((error) => {
        this.invalidLink = true;
        if (error?.error === 'expired-link') {
          this.invalidLinkReason = 'LINK_EXPIRED';
        } else if (error?.error === 'already-used') {
          this.invalidLinkReason = 'LINK_ALREADY_USED';
        } else if (error?.error === 'superseded-link') {
          this.invalidLinkReason = 'LINK_SUPERSEDED';
        } else if (error?.error === 'invalid-link-state') {
          this.invalidLinkReason = 'LINK_INVALID_STATE';
        } else {
          this.invalidLinkReason = 'LINK_INVALID';
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  get passwordMismatch() {
    return this.form.value.password !== this.form.value.confirmPassword;
  }

  isRuleMet(rule: PasswordRule) {
    return rule.test(this.form.value.password || '');
  }

  get canSubmit() {
    return this.form.valid && !this.passwordMismatch;
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.invalid || this.passwordMismatch) {
      return;
    }

    this.loaderService.show();
    this.publicUserPasswordService.setPassword(this.linkId, this.form.value.password)
      .then(() => {
        this.snackBarService.success(this.translateService.instant('USER_PASSWORD.SUCCESS'));
        this.router.navigate(['/login']);
      })
      .catch(error => {
        let key = 'USER_PASSWORD.LINK_INVALID';
        if (error?.error === 'invalid-password') {
          key = 'USER_PASSWORD.INVALID_PASSWORD';
        } else if (error?.error === 'expired-link') {
          key = 'USER_PASSWORD.LINK_EXPIRED';
        } else if (error?.error === 'already-used') {
          key = 'USER_PASSWORD.LINK_ALREADY_USED';
        } else if (error?.error === 'superseded-link') {
          key = 'USER_PASSWORD.LINK_SUPERSEDED';
        } else if (error?.error === 'invalid-link-state') {
          key = 'USER_PASSWORD.LINK_INVALID_STATE';
        }
        this.snackBarService.error(this.translateService.instant(key));
      })
      .finally(() => {
        this.loaderService.hide();
      });
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }
}
